import { useCallback, useEffect, useRef, useState } from 'react';
import { Reorder } from 'framer-motion';

// Gestione del carosello hero della home page (admin, feature 2026-09).
// CRUD semplice su /hero-slides: elenco con riordino drag&drop (stesso pattern
// di ReorderModal per le foto attività), visibilità on/off, form di
// creazione/modifica con upload immagine (riusa /activities/upload-image,
// stesso preset Cloudinary delle attività).

const TOKEN_KEY = 'ticino-wild-token';
const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hero-slides`;
const UPLOAD_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/upload-image`;
const DELETE_IMAGE_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/delete-image`;
const MAX_IMAGE_BYTES = 9 * 1024 * 1024;

const EMPTY_FORM = {
    badge: '',
    title: '',
    subtitle: '',
    buttonText: 'Scopri di più',
    link: '',
    image: null,
    darkenImage: false
};

const authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
    };
};

const HandleHomeCarousel = () => {
    const [slides, setSlides] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    // null = form chiuso; 'new' = nuova slide; altrimenti id della slide in modifica
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [deleteCandidateId, setDeleteCandidateId] = useState(null);
    const fileInputRef = useRef(null);

    const fetchSlides = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE}/all`, { headers: authHeaders() });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const result = await response.json();
            setSlides(result.payload || []);
        } catch (error) {
            console.error(error);
            setErrorMessage('Errore nel caricamento delle slide.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSlides();
    }, [fetchSlides]);

    const openNewForm = () => {
        setEditingId('new');
        setFormData(EMPTY_FORM);
        setErrorMessage('');
    };

    const openEditForm = (slide) => {
        setEditingId(slide._id);
        setFormData({
            badge: slide.badge || '',
            title: slide.title || '',
            subtitle: slide.subtitle || '',
            buttonText: slide.buttonText || '',
            link: slide.link || '',
            image: slide.image || null,
            darkenImage: Boolean(slide.darkenImage)
        });
        setErrorMessage('');
    };

    const closeForm = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
    };

    const handleField = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    // Upload immagine: file → data URL → endpoint upload attività (risposta = oggetto Cloudinary)
    const handleImageFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_IMAGE_BYTES) {
            setErrorMessage('Immagine troppo pesante (massimo 9 MB).');
            return;
        }
        const reader = new FileReader();
        reader.onload = async () => {
            setIsUploading(true);
            setErrorMessage('');
            try {
                const response = await fetch(UPLOAD_URL, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ image: reader.result })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || `HTTP ${response.status}`);
                // Sostituzione: elimino la vecchia immagine da Cloudinary (best-effort)
                if (formData.image?.public_id) {
                    fetch(DELETE_IMAGE_URL, {
                        method: 'DELETE',
                        headers: authHeaders(),
                        body: JSON.stringify({ public_id: formData.image.public_id })
                    }).catch(() => { });
                }
                setFormData(prev => ({ ...prev, image: result.payload }));
            } catch (error) {
                console.error(error);
                setErrorMessage(error.message || "Errore durante il caricamento dell'immagine.");
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        if (formData.image?.public_id) {
            fetch(DELETE_IMAGE_URL, {
                method: 'DELETE',
                headers: authHeaders(),
                body: JSON.stringify({ public_id: formData.image.public_id })
            }).catch(() => { });
        }
        setFormData(prev => ({ ...prev, image: null }));
    };

    const saveSlide = async () => {
        if (!formData.title.trim()) {
            setErrorMessage('Il titolo è obbligatorio.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        const body = JSON.stringify({
            badge: formData.badge.trim() || null,
            title: formData.title.trim(),
            subtitle: formData.subtitle.trim() || null,
            buttonText: formData.buttonText.trim() || null,
            link: formData.link.trim() || null,
            image: formData.image,
            darkenImage: formData.darkenImage
        });
        try {
            const isNew = editingId === 'new';
            const response = await fetch(isNew ? API_BASE : `${API_BASE}/${editingId}`, {
                method: isNew ? 'POST' : 'PATCH',
                headers: authHeaders(),
                body
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || `HTTP ${response.status}`);
            closeForm();
            fetchSlides();
        } catch (error) {
            console.error(error);
            setErrorMessage(error.message || 'Errore durante il salvataggio della slide.');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleVisibility = async (slide) => {
        try {
            const response = await fetch(`${API_BASE}/${slide._id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ isVisible: !slide.isVisible })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            setSlides(prev => prev.map(element =>
                element._id === slide._id ? { ...element, isVisible: !slide.isVisible } : element));
        } catch (error) {
            console.error(error);
            setErrorMessage('Errore nel cambio di visibilità.');
        }
    };

    // Riordino drag&drop: durante il trascinamento aggiorno solo lo stato locale
    // (onReorder scatta a ogni scambio); al rilascio persisto l'ordine corrente.
    // Il ref evita che onDragEnd legga uno stato stantio dalla closure.
    const slidesOrderRef = useRef(slides);
    useEffect(() => {
        slidesOrderRef.current = slides;
    }, [slides]);

    const persistOrder = async () => {
        try {
            const response = await fetch(`${API_BASE}/reorder`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ orderedIds: slidesOrderRef.current.map(slide => slide._id) })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
        } catch (error) {
            console.error(error);
            setErrorMessage("Errore nel salvataggio dell'ordinamento.");
            fetchSlides();
        }
    };

    const deleteSlide = async (slideId) => {
        setDeleteCandidateId(null);
        try {
            const response = await fetch(`${API_BASE}/${slideId}`, {
                method: 'DELETE',
                headers: authHeaders()
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            if (editingId === slideId) closeForm();
            fetchSlides();
        } catch (error) {
            console.error(error);
            setErrorMessage("Errore durante l'eliminazione della slide.");
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg w-full max-w-[1000px] lg:min-w-[1000px]">

            <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-neutral-500">
                    {slides.length} slide · massimo consigliato 4
                </div>
                {
                    editingId === null &&
                    <button
                        type="button"
                        onClick={openNewForm}
                        className="px-4 py-2 rounded-md bg-[#225424] text-white hover:bg-[#1a4019]"
                    >
                        + Aggiungi slide
                    </button>
                }
            </div>

            {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                    {errorMessage}
                </div>
            )}

            {/* Form crea/modifica */}
            {
                editingId !== null &&
                <div className="mb-6 p-4 border-2 border-neutral-200 rounded-xl">
                    <h3 className="font-semibold text-[#225424] mb-3">
                        {editingId === 'new' ? 'Nuova slide' : 'Modifica slide'}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-[#225424] mb-1">Badge (facoltativo)</label>
                                {/* Limite alzato da 30 a 60 (richiesta cliente 2026-09-22: "Esperienza esclusiva TicinoWild"
                                    non ci stava). Il badge va a capo da solo se lungo: meglio restare brevi. */}
                                <input type="text" maxLength={60} value={formData.badge} onChange={handleField('badge')}
                                    placeholder="Es: Partner" className="w-full" />
                                <div className="text-xs text-neutral-500 mt-1">Max 60 caratteri. Testi brevi rendono meglio, soprattutto da mobile.</div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-[#225424] mb-1">Titolo *</label>
                                <input type="text" maxLength={80} value={formData.title} onChange={handleField('title')}
                                    placeholder="Es: DOG SUP EXPERIENCE" className="w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-[#225424] mb-1">Sottotitolo</label>
                                <input type="text" maxLength={100} value={formData.subtitle} onChange={handleField('subtitle')}
                                    placeholder="Es: Riva San Vitale" className="w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-[#225424] mb-1">Testo pulsante</label>
                                <input type="text" maxLength={40} value={formData.buttonText} onChange={handleField('buttonText')}
                                    placeholder="Es: Scopri di più" className="w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-[#225424] mb-1">Link del pulsante</label>
                                <input type="text" value={formData.link} onChange={handleField('link')}
                                    placeholder="Es: /activity-details?id=... oppure https://..." className="w-full" />
                                <div className="text-xs text-neutral-500 mt-1">
                                    Senza link (o senza testo) il pulsante non compare. I link interni aprono la pagina nell'app.
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <label className="block text-sm font-semibold text-[#225424]">Immagine *</label>
                            {
                                formData.image?.secure_url
                                    ? (
                                        // Mini anteprima della slide con i testi correnti
                                        <div className="relative w-full h-[200px] rounded-xl overflow-hidden bg-cover bg-center"
                                            style={{ backgroundImage: `url(${formData.image.resizedUrl || formData.image.secure_url})` }}>
                                            <div className={`absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black/50 ${formData.darkenImage ? 'bg-black/30' : ''}`}></div>
                                            {/* Come in HeroCarousel: badge in alto, titolo+sottotitolo centrati, bottone in basso */}
                                            <div className="absolute inset-0 p-3 flex flex-col items-start">
                                                {formData.badge && <div className="bg-[#225424]/60 text-white text-xs px-1.5 py-0.5 rounded-md shadow">{formData.badge}</div>}
                                                <div className="my-auto flex flex-col items-start gap-0.5">
                                                    <div className="text-white font-bold uppercase drop-shadow">{formData.title || 'Titolo slide'}</div>
                                                    {formData.subtitle && <div className="text-white text-sm drop-shadow">{formData.subtitle}</div>}
                                                </div>
                                                {formData.buttonText && formData.link && <div className="border border-white text-white text-xs px-3 py-1 rounded-full">{formData.buttonText} ›</div>}
                                            </div>
                                        </div>
                                    )
                                    : <div className="w-full h-[200px] rounded-xl border-2 border-dashed border-neutral-300 flex items-center justify-center text-sm text-neutral-400">
                                        Nessuna immagine
                                    </div>
                            }
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading}
                                    className="px-4 py-2 rounded-md border border-[#225424] text-[#225424] hover:bg-neutral-100 disabled:opacity-50"
                                >
                                    {isUploading ? 'Caricamento…' : (formData.image ? 'Cambia immagine' : 'Carica immagine')}
                                </button>
                                {
                                    formData.image &&
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="px-4 py-2 rounded-md border border-red-300 text-red-600 hover:bg-red-50"
                                    >
                                        Rimuovi
                                    </button>
                                }
                            </div>
                            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={formData.darkenImage}
                                    onChange={(event) => setFormData(prev => ({ ...prev, darkenImage: event.target.checked }))}
                                />
                                Scurisci la foto (per far risaltare i testi su immagini chiare)
                            </label>
                            <div className="text-xs text-neutral-500">
                                Foto orizzontale in JPG, lato lungo ~2000px. Senza immagine la slide non compare in home.
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <button type="button" onClick={closeForm} className="px-4 py-2 rounded-md border">Annulla</button>
                        <button
                            type="button"
                            onClick={saveSlide}
                            disabled={isSaving || isUploading}
                            className="px-4 py-2 rounded-md bg-[#225424] text-white hover:bg-[#1a4019] disabled:opacity-50"
                        >
                            {isSaving ? 'Salvataggio…' : 'Salva slide'}
                        </button>
                    </div>
                </div>
            }

            {/* Elenco slide */}
            {isLoading ? (
                <div className="text-sm text-neutral-500">Caricamento…</div>
            ) : slides.length === 0 ? (
                <div className="text-sm text-neutral-500">
                    Nessuna slide: in home resta l'immagine fissa. Aggiungi la prima slide per attivare il carosello.
                </div>
            ) : (
                // Reorder.Group renderizza un <ul>: azzerati padding/margine/bullet di default
                <Reorder.Group axis="y" values={slides} onReorder={setSlides} className="flex flex-col gap-2 list-none !pl-0 !m-0">
                    {slides.map((slide, index) => (
                        <Reorder.Item key={slide._id} value={slide} onDragEnd={persistOrder}
                            className={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 p-2 border rounded-lg bg-white cursor-grab active:cursor-grabbing ${slide.isVisible ? '' : 'opacity-60'}`}>
                            {/* Gruppo maniglia+numero+foto+titolo: su mobile occupa tutta la prima
                                riga (w-full) e i controlli scendono sulla seconda; da md è un'unica riga */}
                            <div className="flex items-center gap-3 w-full md:w-auto md:flex-1 min-w-0">
                                <div className="pb-0.5 pt-1 px-1"><i className="fi fi-br-menu-dots-vertical text-neutral-400"></i></div>
                                <div className="w-6 text-center text-sm text-neutral-500">{index + 1}</div>
                                <div className="w-20 h-12 rounded-md overflow-hidden bg-neutral-100 flex-shrink-0">
                                    {
                                        slide.image?.resizedUrl
                                            ? <img src={slide.image.resizedUrl} alt={slide.title} className="w-full h-full object-cover" />
                                            : <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-400">No foto</div>
                                    }
                                </div>
                                {/* Su mobile il titolo va a capo accanto alla foto (truncate = nowrap
                                    imporrebbe tutta la larghezza del testo e sfonderebbe il viewport);
                                    da md in su torna su una riga con ellissi */}
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold break-words md:truncate">{slide.title}</div>
                                    {slide.subtitle && <div className="text-sm text-neutral-500 break-words md:truncate">{slide.subtitle}</div>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                                <label className="flex items-center gap-1 text-sm cursor-pointer select-none">
                                    <input type="checkbox" checked={slide.isVisible} onChange={() => toggleVisibility(slide)} />
                                    Visibile
                                </label>
                                <button type="button" onClick={() => openEditForm(slide)}
                                    className="px-3 py-1.5 rounded border text-sm hover:bg-neutral-100">
                                    Modifica
                                </button>
                                {
                                    deleteCandidateId === slide._id
                                        ? (
                                            <div className="flex items-center gap-1">
                                                <button type="button" onClick={() => deleteSlide(slide._id)}
                                                    className="px-3 py-1.5 rounded bg-red-600 text-white text-sm">
                                                    Elimina
                                                </button>
                                                <button type="button" onClick={() => setDeleteCandidateId(null)}
                                                    className="px-3 py-1.5 rounded border text-sm">
                                                    Annulla
                                                </button>
                                            </div>
                                        )
                                        : (
                                            <button type="button" aria-label="Elimina slide" onClick={() => setDeleteCandidateId(slide._id)}
                                                className="w-8 h-8 rounded border border-red-300 text-red-600 flex items-center justify-center hover:bg-red-50">
                                                <i className="fi fi-rr-trash flex items-center"></i>
                                            </button>
                                        )
                                }
                            </div>
                        </Reorder.Item>
                    ))}
                </Reorder.Group>
            )}
        </div>
    );
};

export default HandleHomeCarousel;
